import dayjs from "dayjs";
import { useFormikContext } from "formik";
import { CheckInValidation, CheckInValidationType, CutoffCheckInValidation } from "types";

export const useCheckInValidationField = (namePrefix = "") => {
    const { setFieldValue: setFormikFieldValue, setFieldTouched } = useFormikContext();

    const setFieldValue = (name: string, value: any) => {
        const fieldName = `${namePrefix}${name}`;
        setFormikFieldValue(fieldName, value);

        /** Why `setTimeout` is added ? https://github.com/jaredpalmer/formik/issues/2083#issuecomment-639962222 */
        setTimeout(() => setFieldTouched(fieldName));
    };

    const setValidations = (validations: CheckInValidation[]) => {
        setFieldValue("validations", validations);
    };

    return { setFieldValue, setValidations };
};

type MutateAction = "upsert" | "remove";

export const getMutatedValidations = <T extends CheckInValidationType>(
    type: T,
    validations: CheckInValidation[],
    action: MutateAction,
    values: Partial<CheckInValidation<T>>,
): CheckInValidation[] => {
    if (action === "remove") {
        return validations.filter((validation) => validation.object !== type);
    }

    let hasValidation = false;

    const mutatedValidations = validations.map((validation) => {
        if (validation.object === type) {
            hasValidation = true;
            return { ...validation, ...values };
        }

        return validation;
    });

    if (!hasValidation) {
        mutatedValidations.push({ ...(values as CheckInValidation<T>), object: type });
    }

    return mutatedValidations;
};

export const getValidationByType = <T extends CheckInValidationType>(
    type: T,
    validations: CheckInValidation[] = [],
): CheckInValidation<T> | undefined => {
    return validations.find((validation) => validation.object === type) as CheckInValidation<T>;
};

export const getDurationMinutes = (validation?: CutoffCheckInValidation) => {
    return dayjs.duration(validation?.duration ?? "").asMinutes();
};
